import { cleanName, cleanProfessionalDisplayText } from "@/lib/utils/nameUtils";
import { normalizeProfileLocation } from "@/lib/search/profileLocation";
import { readCompanyMetadata } from "@/lib/search/companyMatching";

const URL_PREFIX_RE = /^(?:https?:\/\/|www\.|linkedin\.com\b)/i;
const URL_TOKEN_RE = /^(?:https?|www|linkedin|linkedin\.com|com)$/i;

const sanitizeCompanyLabel = (value) => {
  const clean = cleanProfessionalDisplayText(String(value || "").trim());
  if (!clean) return "";
  if (URL_PREFIX_RE.test(clean)) return "";
  if (URL_TOKEN_RE.test(clean.toLowerCase())) return "";
  return clean;
};

export const transformSearchResults = (hits, enriched = false) => {
  return hits
    .map((hit) => {
      const companyFromCompany = sanitizeCompanyLabel(
        hit["company.name"] || "",
      );
      const companyFromAffiliation = sanitizeCompanyLabel(
        hit["profile.affiliation_name"] || "",
      );
      const resolvedCompany = companyFromCompany || companyFromAffiliation;

      const jobTitle = cleanProfessionalDisplayText(
        hit["linkedin.title"] || hit["linkedin.headline"] || "",
      );

      return {
        key: hit.id,
        id: hit.id,
        sub: hit.sub,
        objectID: hit.objectID || null,
        vanity: hit.vanity,
        enriched: enriched,
        url: hit["profile.url"],
        title: hit["profile.title"] || "",
        jobTitle,
        name: `${cleanName(hit["profile.given_name"] || "")} ${cleanName(
          hit["profile.family_name"] || "",
        )}`.trim(),
        given_name: cleanName(hit["profile.given_name"] || ""),
        family_name: cleanName(hit["profile.family_name"] || ""),
        expertise: hit["profile.expertise"] || [],
        avatar: `${cleanName(hit["profile.given_name"] || "")?.charAt(0) || ""}${
          cleanName(hit["profile.family_name"] || "")?.charAt(0) || ""
        }`,
        picture: hit["profile.picture"],
        picture_custom: hit["profile.picture_custom"] || null,
        picture_cached: hit["profile.picture_cached"] || null,
        picture_face_ok: hit["profile.picture_face_ok"] ?? null,
        ...normalizeProfileLocation(hit),
        companyAff: companyFromAffiliation,
        companyAffUrl: hit["profile.affiliation_url"] || "",
        linkedinUrl: hit["profile.linkedin_url"],

        hourlyRate: hit["profile.hourly_rate"],
        halfRate: hit["profile.half_rate"],

        sessionTypes: hit["profile.sessionTypes"],
        estimatedRate60:
          hit["profile.sessionTypes"]?.["60min"]?.estimatedRate || 0,
        estimatedRate30:
          hit["profile.sessionTypes"]?.["30min"]?.estimatedRate || 0,
        sessionRate60: hit["profile.sessionTypes"]?.["60min"]?.rate || 0,
        sessionRate30: hit["profile.sessionTypes"]?.["30min"]?.rate || 0,
        rateSettings: hit["profile.rateSettings"],
        meetingTypes: hit["profile.meetingTypes"],

        acceptRate: hit["profile.accept_percent"],
        responseRate: hit["profile.response_rate"],
        is_public: hit["profile.is_public"],
        company: resolvedCompany,
        companyDetails: readCompanyMetadata(hit),
        companyUrl: companyFromCompany
          ? hit["company.url"] || ""
          : companyFromAffiliation
            ? hit["profile.affiliation_url"] || ""
            : "",
        // Quality signals — read directly from linkedin.* (indexed in Algolia)
        // verifi_badge is the legacy field name used in older user records
        premium: hit["linkedin.premium"] ?? false,
        verified:
          hit["linkedin.verified"] ?? hit["linkedin.verifi_badge"] ?? false,
        followerCount: hit["linkedin.follower"] ?? null,

        stats: {
          meeting_stats: {
            received: {
              requested: hit["stats.meeting_stats.received.requested"] || 0,
              dismissed: hit["stats.meeting_stats.received.dismissed"] || 0,
              completed: hit["stats.meeting_stats.received.completed"] || 0,
            },
            sent: {
              completed: hit["stats.meeting_stats.sent.completed"] || 0,
            },
          },
        },
      };
    })
    .filter((profile) => {
      return profile.is_public !== false;
    });
};
